import {
  type AskPunttAgentStep,
  type AskPunttMessage,
  type AskPunttProjectData,
  type AskPunttProjectStage,
  type AskPunttSearchProgress,
  type ReviewChecklistItem,
} from "@mg/schemas/src/christo/askPuntt";

import { MOCK_SEARCH_SOURCES } from "./mockData";

import { type ProjectFeedEvent } from "../../services/askPunttProjectFeed.types";

const TS = "2024-01-01T00:00:00Z";

const baseAssistantMessage = (): AskPunttMessage => ({
  _id: "msg-style-guide",
  chatId: "chat-style-guide",
  role: "assistant",
  content: "",
  createdAt: TS,
});

export const THINKING_PHASE_LABELS = [
  "Phase 0 — Thinking (no message yet)",
  "Phase 1 — Reading attachments",
  "Phase 2 — Researching source",
  "Phase 3 — Analyzing (agent steps)",
  "Phase 4 — Writing response (streaming)",
] as const;

export const THINKING_PHASE_MESSAGES: {
  label: (typeof THINKING_PHASE_LABELS)[number];
  message?: AskPunttMessage;
  precedingUserHasAttachments?: boolean;
}[] = [
  { label: THINKING_PHASE_LABELS[0] },
  {
    label: THINKING_PHASE_LABELS[1],
    message: baseAssistantMessage(),
    precedingUserHasAttachments: true,
  },
  {
    label: THINKING_PHASE_LABELS[2],
    message: {
      ...baseAssistantMessage(),
      searchProgress: [{ source: "Enterprise Knowledge", status: "searching" }],
    },
  },
  {
    label: THINKING_PHASE_LABELS[3],
    message: {
      ...baseAssistantMessage(),
      agentSteps: [
        {
          type: "tool_call",
          tool: "puntt_search",
          displayName: "Search Knowledge",
          description: "Searching regulatory database",
          timestamp: TS,
        },
      ],
    },
  },
  {
    label: THINKING_PHASE_LABELS[4],
    message: {
      ...baseAssistantMessage(),
      streamingContent: "Based on the regulatory review, the claim needs…",
    },
  },
];

export const AGENT_STEPS_STREAMING: AskPunttAgentStep[] = [
  {
    type: "tool_call",
    tool: "analyze",
    displayName: "Analyze",
    description: "Analyzing document structure",
    timestamp: TS,
  },
];

export const AGENT_STEPS_COMPLETE: AskPunttAgentStep[] = [
  {
    type: "tool_call",
    tool: "puntt_search",
    displayName: "Search Knowledge",
    description: "Searching knowledge base",
    timestamp: TS,
  },
  {
    type: "tool_result",
    tool: "puntt_search",
    displayName: "Search Knowledge",
    summary: "Found 5 relevant documents in the knowledge base",
    timestamp: "2024-01-01T00:00:01Z",
  },
];

export const AGENT_STEPS_CREATE_TICKET_IN_FLIGHT: AskPunttAgentStep[] = [
  {
    type: "tool_call",
    tool: "create_ticket",
    displayName: "Create Ticket",
    statusName: "Creating review ticket",
    description: "Generating review for asset",
    timestamp: TS,
  },
];

export const AGENT_STEPS_COLLAPSED_GROUP: AskPunttAgentStep[] = [
  {
    type: "tool_call",
    tool: "search",
    displayName: "Search",
    description: "Search 1",
    timestamp: TS,
  },
  {
    type: "tool_result",
    tool: "search",
    displayName: "Search",
    summary: "Found 3 results",
    timestamp: "2024-01-01T00:00:01Z",
  },
  {
    type: "tool_call",
    tool: "search",
    displayName: "Search",
    description: "Search 2",
    timestamp: "2024-01-01T00:00:02Z",
  },
  {
    type: "tool_result",
    tool: "search",
    displayName: "Search",
    summary: "Found 5 results",
    timestamp: "2024-01-01T00:00:03Z",
  },
  {
    type: "tool_call",
    tool: "search",
    displayName: "Search",
    description: "Search 3",
    timestamp: "2024-01-01T00:00:04Z",
  },
  {
    type: "tool_result",
    tool: "search",
    displayName: "Search",
    summary: "Found 2 results",
    timestamp: "2024-01-01T00:00:05Z",
  },
];

export const SEARCH_PROGRESS_MIXED: AskPunttSearchProgress[] = [
  { source: "Puntt Regulatory Database", status: "searching" },
  ...MOCK_SEARCH_SOURCES.slice(0, 2),
];

export const CHAT_MESSAGE_STREAMING: AskPunttMessage = {
  ...baseAssistantMessage(),
  status: "pending",
  streamingContent:
    "The phrase **with active probiotics** implies a health benefit that is not authorized under EU Regulation 1924/2006.",
};

export const CHAT_MESSAGE_WITH_TOOLS: AskPunttMessage = {
  ...baseAssistantMessage(),
  status: "pending",
  agentSteps: AGENT_STEPS_COMPLETE,
  searchProgress: SEARCH_PROGRESS_MIXED,
  streamingContent: "",
};

export const REVIEW_CHECKLIST_DYNAMIC_IN_PROGRESS: ReviewChecklistItem[] = [
  {
    label: "Brand Guidelines",
    sub: "Visual standards, identity, tone of voice",
  },
  {
    label: "Visual Quality",
    sub: "Composition, imagery, typography, design",
  },
  {
    label: "Content Review",
    sub: "Copy, claims, messaging, accuracy",
  },
];

const baseProjectData = {
  name: "Style Guide Project",
  markets: ["US"],
  projectType: "artwork",
  routing: [] as never[],
  forecast: { cycleTimeDays: 5, vsTargetDays: 0, confidence: 80 },
};

export const PROJECT_DATA_NEEDS_REVIEW: AskPunttProjectData = {
  ...baseProjectData,
  stages: [
    { name: "Brand", status: "done", color: "green" },
    { name: "Regulatory", status: "needs_review", color: "amber" },
  ],
};

export const PROJECT_DATA_NOTIFIED: AskPunttProjectData = {
  ...baseProjectData,
  stages: [
    { name: "Brand", status: "done", color: "green" },
    { name: "Legal", status: "needs_review", color: "amber" },
  ],
};

export const PROJECT_DATA_ACTIVE: AskPunttProjectData = {
  ...baseProjectData,
  stages: [
    { name: "Brand", status: "active", color: "amber" },
    { name: "Legal", status: "pending", color: "gray" },
  ],
};

export const PROJECT_DATA_BLOCKED: AskPunttProjectData = {
  ...baseProjectData,
  stages: [{ name: "Regulatory", status: "blocked", color: "red" }],
};

export const STAGE_BAR_STATES: AskPunttProjectStage[] = [
  { name: "Brand", status: "done", color: "green" },
  { name: "Legal", status: "active", color: "amber" },
  { name: "Regulatory", status: "needs_review", color: "red" },
  { name: "Final", status: "blocked", color: "red" },
  { name: "QA", status: "pending", color: "gray" },
];

export const FEED_SUGGESTION_RESOLVED: ProjectFeedEvent = {
  _id: "feed-sg-1",
  projectId: "proj-sg",
  type: "suggestion_issued",
  actor: { kind: "puntt" },
  body: "Route this asset to Legal before launch",
  visibility: "all",
  createdAt: TS,
  payload: { resolution: "approve", reason: "Legal must sign off" },
};
